import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, Crown, Loader2, LogOut, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const FEATURES = [
  'Calendrier & rendez-vous illimités',
  'Devis & Factures (HT/TTC, TVA libre)',
  'Compte rendu, Charges & Bénéfice',
  'Module Prévisionnel + Météo',
  'Exports Excel & PDF détaillés',
  'Support prioritaire',
];

export const SubscriptionPage = () => {
  const { user, api, logout, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(null);

  useEffect(() => {
    api.get('/subscription/plans').then((r) => {
      setPlans(r.data.plans || []);
    }).catch(() => toast.error('Erreur chargement tarifs')).finally(() => setLoading(false));
  }, [api]);

  // Polling logic on success page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (!sid || !window.location.pathname.includes('/subscription/success')) return;
    let attempts = 0;
    const max = 12;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const r = await api.get(`/subscription/status/${sid}`);
        if (r.data.payment_status === 'paid') {
          clearInterval(interval);
          toast.success('Paiement confirmé ! Abonnement activé.');
          await refreshUser();
          // Clear query string then navigate home
          window.history.replaceState({}, '', '/');
          window.location.href = '/';
        } else if (r.data.status === 'expired' || attempts >= max) {
          clearInterval(interval);
          if (attempts >= max) toast.warning('Vérification du paiement trop longue. Réessayez dans un instant.');
        }
      } catch (_e) {
        if (attempts >= max) clearInterval(interval);
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [api, refreshUser]);

  const handleSubscribe = async (planId) => {
    setRedirecting(planId);
    try {
      const r = await api.post('/subscription/checkout', {
        plan: planId,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création du paiement');
      setRedirecting(null);
    }
  };

  const isSuccessPage = window.location.pathname.includes('/subscription/success');
  const reason = user?.access_reason;
  const trialEnd = user?.trial_ends_at ? format(parseISO(user.trial_ends_at), 'dd/MM/yyyy', { locale: fr }) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-emerald-50/40" data-testid="subscription-page">
      <header className="px-6 py-4 flex items-center justify-between bg-white/70 backdrop-blur border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-[#065F46]" />
          <span className="font-outfit font-bold text-xl text-[#065F46]">Ledgerly</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-slate-600">{user?.email}</span>
          <Button variant="outline" size="sm" onClick={logout} data-testid="sub-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {isSuccessPage ? (
          <Card className="max-w-xl mx-auto" data-testid="success-card">
            <CardContent className="p-8 text-center">
              <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-4" />
              <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-2">Paiement en cours de vérification</h2>
              <p className="text-slate-600">Merci pour votre paiement. Nous validons la transaction Stripe…</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="text-center mb-10">
              <h1 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 mb-3">
                {reason === 'TRIAL_EXPIRED' && 'Votre essai gratuit est terminé'}
                {reason === 'SUBSCRIPTION_EXPIRED' && 'Votre abonnement est expiré'}
                {(reason === 'NO_SUBSCRIPTION' || !reason) && 'Choisissez votre abonnement'}
              </h1>
              <p className="text-slate-600 max-w-2xl mx-auto">
                {reason === 'TRIAL_EXPIRED' || reason === 'SUBSCRIPTION_EXPIRED'
                  ? 'Pour continuer à utiliser Ledgerly, choisissez un abonnement. Vos données sont conservées.'
                  : 'Accédez à toutes les fonctionnalités de Ledgerly.'}
                {trialEnd && reason === 'trial' && ` Votre essai expire le ${trialEnd}.`}
              </p>
            </div>

            {loading ? (
              <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" /></div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {plans.map((p) => {
                  const isYearly = p.id === 'yearly';
                  const monthly = isYearly ? (p.amount / 12) : p.amount;
                  return (
                    <Card
                      key={p.id}
                      className={`relative ${isYearly ? 'border-2 border-[#065F46] shadow-lg' : 'border border-slate-200'}`}
                      data-testid={`plan-${p.id}`}
                    >
                      {isYearly && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#065F46] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                          <Crown className="w-3 h-3" /> ÉCONOMIQUE
                        </div>
                      )}
                      <CardHeader>
                        <CardTitle className="font-outfit">
                          {p.label}
                        </CardTitle>
                        <div className="mt-2">
                          <span className="font-outfit text-4xl font-bold text-slate-900 font-mono">
                            {p.amount.toFixed(2).replace('.', ',')} €
                          </span>
                          <span className="text-slate-500 ml-2 text-sm">
                            /{p.id === 'monthly' ? 'mois' : 'an'}
                          </span>
                          {isYearly && (
                            <p className="text-xs text-emerald-700 mt-1 font-medium">
                              soit {monthly.toFixed(2).replace('.', ',')} €/mois — économisez ~{Math.round(((19.99 * 12) - p.amount) / (19.99 * 12) * 100)}%
                            </p>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2 mb-6">
                          {FEATURES.map((f) => (
                            <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
                            </li>
                          ))}
                        </ul>
                        <Button
                          className={`w-full ${isYearly ? 'bg-[#065F46] hover:bg-[#047857]' : 'bg-slate-900 hover:bg-slate-800'} text-white`}
                          onClick={() => handleSubscribe(p.id)}
                          disabled={!!redirecting}
                          data-testid={`subscribe-${p.id}-btn`}
                        >
                          {redirecting === p.id ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirection vers Stripe…</>
                          ) : (
                            `S'abonner — ${p.amount.toFixed(2).replace('.', ',')} €`
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            <p className="text-center text-xs text-slate-500 mt-8">
              Paiement sécurisé via Stripe. Vous pouvez résilier à tout moment.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default SubscriptionPage;
